import type { ReactNode } from "react";
import type { Role } from "../lib/api";

// The shared refusal pattern (Session AQT, Phase 1 design system foundation, P-2). Before this,
// a role-restricted page or control used one of three idioms: hide entirely with no explanation
// (GeneratePpmButton), a silent redirect that looks like a bug rather than a boundary
// (CoverageZonesPage, its own comment called this "the standard admin-gate pattern used across
// the office app"), or an in-place sentence (SettingsPage's RecentSmsSection). This converges on
// the third: clear, in place, never a bare 403 and never a dead-end bounce.
//
// Deliberately not a blanket sweep of every refusal site in the app -- built here and applied to
// this session's own P-1/nav work (CoverageZonesPage); the remaining sites are named as remaining
// scope, not silently left inconsistent.

export interface AccessRestrictedProps {
  message?: string;
  className?: string;
}

export function AccessRestricted({
  message = "This is only available to admin accounts.",
  className = "",
}: AccessRestrictedProps) {
  return (
    <div
      role="status"
      className={`rounded-ele border border-ele-hairline bg-ele-surface px-4 py-3 text-sm text-ele-body ${className}`.trim()}
    >
      {message}
    </div>
  );
}

export interface RequireRoleProps {
  role: Role | undefined;
  allow: Role[];
  message?: string;
  className?: string;
  children: ReactNode;
}

// Renders `children` when the caller's role is in `allow`, otherwise the one refusal pattern
// above. `role` is `undefined`-safe (a session still loading, or logged out) and fails closed --
// shown only once the role is known AND allowed.
export function RequireRole({ role, allow, message, className, children }: RequireRoleProps) {
  if (role && allow.includes(role)) return <>{children}</>;
  return <AccessRestricted message={message} className={className} />;
}
